import { Stack, XStack } from '@mythical/ui'
import useEmblaCarousel from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import React, { useCallback, useEffect, useImperativeHandle, useMemo } from 'react'
import {
  ContentCarouselProps,
  defaultSizeConfig,
} from './ContentCarouselProps'

const ContentCarouselComponent = <T extends {},>(
  props: ContentCarouselProps<T>,
) => {
  const {
    items = [],
    keyExtractor,
    renderItem,
    controllerRef,
    initialScrollIndex = 0,
    sizeConfig = defaultSizeConfig,
  } = props

  const plugins = useMemo(() => [WheelGesturesPlugin()], [])

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      slidesToScroll: 1,
      containScroll: 'trimSnaps',
      skipSnaps: true,
      align: 'start',
      duration: 20,
      startIndex: initialScrollIndex,
    },
    plugins,
  )

  const updateControllerState = useCallback(() => {
    if (!emblaApi || !controllerRef) return
    controllerRef._updateState({
      currentIndex: emblaApi.selectedScrollSnap(),
      canScrollPrevious: emblaApi.canScrollPrev(),
      canScrollNext: emblaApi.canScrollNext(),
    })
  }, [emblaApi, controllerRef])

  useEffect(() => {
    if (!emblaApi || !controllerRef) return
    updateControllerState()
    emblaApi.on('select', updateControllerState)
    emblaApi.on('reInit', updateControllerState)
    return () => {
      emblaApi.off('select', updateControllerState)
      emblaApi.off('reInit', updateControllerState)
    }
  }, [emblaApi, controllerRef, updateControllerState])

  useImperativeHandle(
    controllerRef?.ref,
    () => ({
      scrollToIndex: (index: number) => {
        if (items.length === 0) return
        const clampedIndex = Math.max(0, Math.min(index, items.length - 1))
        emblaApi?.scrollTo(clampedIndex)
      },
      scrollToNext: () => {
        if (items.length === 0) return
        emblaApi?.scrollNext()
      },
      scrollToPrevious: () => {
        if (items.length === 0) return
        emblaApi?.scrollPrev()
      },
    }),
    [emblaApi, items.length],
  )

  if (items.length === 0) return null

  return (
    <Stack
      flex={1}
      ml={sizeConfig.spacing * -1}
      $gtXs={{ ml: sizeConfig.$gtXs.spacing * -1 }}
      $gtSm={{ ml: sizeConfig.$gtSm.spacing * -1 }}
      ref={emblaRef as any}
    >
      <XStack height="auto" style={{ backfaceVisibility: 'hidden' }}>
        {items.map((item, i) => (
          <Stack
            key={keyExtractor(item, i)}
            miw={0}
            fg={0}
            fs={0}
            w={sizeConfig.width}
            pl={sizeConfig.spacing}
            $gtXs={{
              w: sizeConfig.$gtXs.width,
              pl: sizeConfig.$gtXs.spacing,
            }}
            $gtSm={{
              w: sizeConfig.$gtSm.width,
              pl: sizeConfig.$gtSm.spacing,
            }}
          >
            {renderItem(item, i)}
          </Stack>
        ))}
      </XStack>
    </Stack>
  )
}

export const ContentCarousel = React.memo(
  ContentCarouselComponent,
) as typeof ContentCarouselComponent
